import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Search, Filter, ChevronDown, ChevronUp, X } from "lucide-react";

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState(params.get("type") || "all");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCats, setShowCats] = useState(false);

  useEffect(() => {
    setType(params.get("type") || "all");
    setCategory(params.get("category") || "all");
  }, [params]);

  useEffect(() => {
    setLoading(true);
    const q_params = {};
    if (type !== "all") q_params.type = type;
    if (q) q_params.q = q;
    if (category !== "all") q_params.category = category;
    // Banking tab: also surface loans_only lenders (Poonawalla / Ram Fincorp)
    // that live primarily on loans.offer.express, so users browsing the
    // Banking category on the main site can discover them here too.
    if (type === "banking") q_params.channel = "all";
    api.get("/products", { params: q_params }).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [type, q, category]);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    return ["all", ...Array.from(set)];
  }, [products]);

  const handleTypeChange = (t) => {
    setType(t);
    if (t === "all") params.delete("type"); else params.set("type", t);
    setParams(params, { replace: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-6">
        <div>
          <div className="badge-mono text-neutral-500 mb-2">Marketplace</div>
          <h1 className="font-display text-5xl font-bold tracking-tighter">All brands</h1>
          <p className="text-neutral-600 mt-3 max-w-lg">Shop your favourite brands and let us track your cashback automatically. Every click carries your unique referral id end-to-end.</p>
        </div>
        <div className="w-full md:w-96">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              data-testid="marketplace-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, providers…"
              className="pl-9 h-11 bg-white"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Tabs value={type} onValueChange={handleTypeChange}>
          <TabsList data-testid="type-tabs">
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="banking" data-testid="tab-banking">Banking</TabsTrigger>
            <TabsTrigger value="ecom" data-testid="tab-ecom">Commerce</TabsTrigger>
          </TabsList>
        </Tabs>

        <button
          type="button"
          data-testid="toggle-categories"
          onClick={() => setShowCats((s) => !s)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.1em] bg-white border border-black/10 hover:border-black transition-colors"
        >
          <Filter className="w-3.5 h-3.5" />
          {category === "all" ? "Filter by category" : `Category: ${category.replace("_", " ")}`}
          {showCats ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>

        {category !== "all" && (
          <button
            type="button"
            data-testid="clear-category"
            onClick={() => setCategory("all")}
            className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-black"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}

        <div className="ml-auto">
          <Badge variant="secondary" data-testid="results-count" className="font-mono-accent">
            {products.length} results
          </Badge>
        </div>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white border border-black/8 rounded-lg overflow-hidden">
              <div className="aspect-[16/10] bg-neutral-100 animate-pulse" />
              <div className="p-5 space-y-3">
                <div className="h-4 bg-neutral-100 rounded animate-pulse w-1/3" />
                <div className="h-6 bg-neutral-100 rounded animate-pulse w-3/4" />
                <div className="h-4 bg-neutral-100 rounded animate-pulse w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : products.length ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      ) : (
        <div className="text-center py-20 text-neutral-500" data-testid="no-results">
          <p className="text-lg">No products match your search.</p>
        </div>
      )}

      {/* Expandable category picker anchored at the bottom */}
      {showCats && (
        <div
          data-testid="category-panel"
          className="mt-10 p-5 rounded-xl border border-black/10 bg-white"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="badge-mono text-neutral-500 flex items-center gap-2">
              <Filter className="w-3.5 h-3.5" /> Filter by category
            </div>
            <button
              type="button"
              data-testid="close-categories"
              onClick={() => setShowCats(false)}
              className="text-neutral-500 hover:text-black"
              aria-label="Close category filter"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`cat-${c}`}
                onClick={() => { setCategory(c); setShowCats(false); }}
                className={`px-3 py-1 rounded-full text-xs uppercase tracking-[0.1em] border transition-colors ${
                  category === c
                    ? "bg-black text-white border-black"
                    : "bg-white text-neutral-600 border-black/10 hover:border-black"
                }`}
              >
                {c.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
